import React, { ReactElement, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { AddressUS, PersonName } from '@/typings/questionnaires-api-models';
import CaretCircleDown from '@/components/Icons/CaretCircleDown';
import CheckCircle from '@/components/Icons/CheckCircle';
import Spacer from '@/components/Spacer';
import XCircle from '@/components/Icons/XCircle';
import MinusCircle from '@/components/Icons/MinusCircle';
import * as format from '@format';
import Tooltip from '@/components/Tooltip';

import s from './MedianIncomeWidget.module.scss';

type MedianIncomeType = {
  currentMonthlyIncome?: number | null;
  currentAnnualizedIncome?: number | null;
  medianHouseholdIncome?: number | null;
  deductions?: number | null;
  isOverMedian?: boolean | null;
  name?: PersonName | null;
  address?: AddressUS | null ;
  householdSize?: number | null;
  dateOfFiling?: Date | null;
};

type MedianIncomeProps = {
  value: MedianIncomeType | null;
  cmiAutofillDescription?: string | null;
};

const DEFAULT_NULL_CHAR = '-';

const MedianIncomeWidget: React.FC<MedianIncomeProps> = ({
  cmiAutofillDescription,
  value,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const detailsRef = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    detailsRef.current?.addEventListener('toggle', () => {
      setIsOpen(!isOpen);
    });
  }, [setIsOpen]);

  const getIcon = (b?: boolean | null): ReactElement => {
    if (b === true) {
      return <CheckCircle weight="fill" size={32} color="var(--color-success)" />;
    }
    if (b === false) {
      return <XCircle weight="fill" size={32} color="var(--color-error)" />;
    }
    return <MinusCircle weight="fill" size={32} color="var(--color-gray-50)" />;
  };

  const cmiOverMedianInt = value?.currentAnnualizedIncome && value?.medianHouseholdIncome
    ? ((value.currentAnnualizedIncome - value.medianHouseholdIncome) / 12) - (value.deductions || 0)
    : 0;

  return (
    <div className={s.wrapper}>
      <details ref={detailsRef} className={classNames(s['collapsible-area'], '')} open={isOpen}>
        <summary onClick={(e) => e.stopPropagation()}>
          <div className={s.content}>
            <div className={s.halfWidth}>
              {getIcon(cmiOverMedianInt !== 0 ? cmiOverMedianInt < 0 : null)}
              <div className={s.column}>
                {cmiAutofillDescription
                  ? <Tooltip text={cmiAutofillDescription}>
                    <span className='body-sm'>CMI Over Median</span>
                  </Tooltip>
                  : <span className='body-sm'>CMI Over Median</span>}
                <span className='body-md-bold'>
                  {value?.currentAnnualizedIncome && value?.medianHouseholdIncome
                    ? format.price.withCurrency(((value.currentAnnualizedIncome - value.medianHouseholdIncome) / 12) - (value.deductions || 0), 'USD')
                    : DEFAULT_NULL_CHAR}
                </span>
              </div>
            </div>
            <div className={s.quarterWidth}>
              <span className='body-sm'>Current Monthly Income</span>
              <span className='body-md-bold'>
                {value?.currentMonthlyIncome ? `$${value?.currentMonthlyIncome}` : DEFAULT_NULL_CHAR}
              </span>
            </div>
            <div className={s.fifthWidth}>
              <span className='body-sm'>Deductions</span>
              <span className='body-md-bold'>
                {value?.deductions ? `$${value?.deductions}` : DEFAULT_NULL_CHAR}
              </span>
            </div>
          </div>
          <CaretCircleDown weight='fill' size={16} />
        </summary>
        <div className={s.detailsWrapper}>
          <div className={s.halfDetails}>
            <span className='body-sm-bold'>Your info</span>
            <Spacer size={8}/>
            <div className={s.field}>
              <span className={s.value}>Name</span>
              <span className={s.answer}>{value?.name || DEFAULT_NULL_CHAR}</span>
            </div>
            <div className={s.field}>
              <span className={s.value}>Address</span>
              <span className={s.answer}>{value?.address || DEFAULT_NULL_CHAR}</span>
            </div>
            <div className={s.field}>
              <span className={s.value}>Household Size</span>
              <span className={s.answer}>{value?.householdSize || DEFAULT_NULL_CHAR}</span>
            </div>
            <div className={s.field}>
              <span className={s.value}>Date of Filing</span>
              <span className={s.answer}>{value?.dateOfFiling || new Date().toLocaleString('en-US', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}</span>
            </div>
          </div>
          <div className={s.halfDetails}>
            <span className='body-sm-bold'>122A-1: Part 2</span>
            <Spacer size={8}/>
            <div className={s.field}>
              <span className={s.value}>12.a - Current monthly income</span>
              <span className={s.answer}>{value?.currentMonthlyIncome ? format.price.withCurrency(value.currentMonthlyIncome, 'USD') : DEFAULT_NULL_CHAR}</span>
            </div>
            <div className={s.field}>
              <span className={s.value}>12.b - Current annualized income</span>
              <span className={s.answer}>{value?.currentAnnualizedIncome ? format.price.withCurrency(value.currentAnnualizedIncome, 'USD') : DEFAULT_NULL_CHAR}</span>
            </div>
            <div className={s.field}>
              <span className={s.value}>13 - Median household income</span>
              <span className={s.answer}>{value?.medianHouseholdIncome ? format.price.withCurrency(value.medianHouseholdIncome, 'USD') : DEFAULT_NULL_CHAR}</span>
            </div>
            <div className={s.field}>
              <span className={s.value}>14 - Current income is over/under median income</span>
              <span className={s.answer}>{value?.currentAnnualizedIncome && value?.medianHouseholdIncome
                ? format.price.withCurrency(value.currentAnnualizedIncome - value.medianHouseholdIncome, 'USD')
                : DEFAULT_NULL_CHAR}</span>
            </div>
          </div>
        </div>
      </details>
    </div>
  );
};

export default MedianIncomeWidget;
